import { useMemo } from "react";
import { Link } from "react-router-dom";
import { Box, Card, Group, SimpleGrid, Skeleton, Stack, Text, ThemeIcon, Title, UnstyledButton } from "@mantine/core";
import { BookOpen } from "lucide-react";
import { GuideProgress } from "@nodus/ui";
import { faqIcon } from "@/lib/faq-icons";
import { guideMatches, sortGuides } from "@/lib/guides";
import { useGuidesRealtime, useMyGuides } from "@/hooks/use-guides";
import { useGuideLink } from "@/hooks/use-guide-modal";

/**
 * Guides on the Office Guide page (`/faq`), above the FAQ: one card per guide
 * with its progress, unfinished ones first. Hidden when there are none (or the
 * search matches none); a failed load hides it too, the FAQ stays usable.
 */
export function GuidesSection({ search = "" }: { search?: string }) {
  const guideLink = useGuideLink();
  useGuidesRealtime();
  const { data, isLoading } = useMyGuides();
  const guides = useMemo(
    () => sortGuides((data?.guides ?? []).filter((g) => guideMatches(g, search))),
    [data, search],
  );

  if (isLoading) {
    return (
      <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="md">
        <Skeleton height={120} radius="md" />
        <Skeleton height={120} radius="md" />
      </SimpleGrid>
    );
  }
  if (guides.length === 0) return null;

  return (
    <Stack gap="sm" component="section" aria-labelledby="office-guide-guides">
      <Box>
        <Title order={3} id="office-guide-guides">
          Guides
        </Title>
        <Text fz="sm" c="dimmed">
          Help to get set up, one step at a time.
        </Text>
      </Box>
      <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="md">
        {guides.map((g) => {
          const Icon = g.icon ? faqIcon(g.icon) : BookOpen;
          return (
            <UnstyledButton key={g.key} component={Link} {...guideLink(g.key)} aria-label={`Open guide: ${g.title}`}>
              <Card withBorder padding="md" radius="md" h="100%">
                <Stack gap="sm" justify="space-between" h="100%">
                  <Group gap="sm" wrap="nowrap" align="flex-start">
                    <ThemeIcon variant="light" size={36} radius="md">
                      <Icon size={20} />
                    </ThemeIcon>
                    <Stack gap={2} miw={0}>
                      <Text fw={600} lineClamp={2}>
                        {g.title}
                      </Text>
                      {g.summary && (
                        <Text fz="sm" c="dimmed" lineClamp={2}>
                          {g.summary}
                        </Text>
                      )}
                    </Stack>
                  </Group>
                  <GuideProgress done={g.done} total={g.total} status={g.status} />
                </Stack>
              </Card>
            </UnstyledButton>
          );
        })}
      </SimpleGrid>
    </Stack>
  );
}
